<template>
  <page-header-wrapper>
    <div class="sales-setup">
      <div class="setup-heading">
        <h2>{{ sectionMeta.title }}</h2>
        <p>{{ sectionMeta.description }}</p>
      </div>
      <a-tabs v-model="activeTab" :animated="false" @change="handleTabChange">
        <a-tab-pane key="warehouse" tab="仓库档案">
          <div class="section-toolbar">
            <div><h3>仓库</h3><span>仅启用仓库可用于新销售订单</span></div>
            <a-button type="primary" :disabled="warehouseEditing" @click="startWarehouse">新增仓库</a-button>
          </div>
          <div v-if="warehouseEditing" class="inline-editor">
            <a-input v-model="warehouseDraft.warehouseCode" maxlength="64" placeholder="仓库编码" />
            <a-input v-model="warehouseDraft.warehouseName" maxlength="128" placeholder="仓库名称" />
            <a-input v-model="warehouseDraft.remark" maxlength="500" placeholder="备注（选填）" />
            <a-button type="primary" :loading="warehouseSaving" @click="submitWarehouse">保存</a-button>
            <a-button @click="warehouseEditing = false">取消</a-button>
          </div>
          <a-table :columns="warehouseColumns" :data-source="warehouses" :loading="warehouseLoading" row-key="id" :pagination="false" :locale="{ emptyText: '尚无仓库，请新增首个出库仓' }">
            <template slot="warehouseStatus" slot-scope="value"><a-tag :color="value === 'enabled' ? 'green' : 'default'">{{ value === 'enabled' ? '启用' : '停用' }}</a-tag></template>
            <template slot="warehouseActions" slot-scope="value, record">
              <a @click="editWarehouse(record)">编辑</a><a-divider type="vertical" />
              <a @click="toggleWarehouse(record)">{{ record.status === 'enabled' ? '停用' : '启用' }}</a>
            </template>
          </a-table>
        </a-tab-pane>
        <a-tab-pane key="price" tab="商品指导价">
          <div class="section-toolbar">
            <div><h3>选择商品</h3><span>固定指导价按 SKU 和仓库维护；多个可用价格由销售订单选择</span></div>
            <a-select v-model="selectedSkuId" show-search allow-clear :filter-option="false" style="width:350px" placeholder="搜索已启用 SKU 编码或名称" @focus="searchSku('')" @search="searchSku">
              <a-select-option v-for="sku in skuList" :key="sku.id" :value="sku.id">{{ sku.skuCode }} · {{ sku.skuName }}</a-select-option>
            </a-select>
          </div>
          <guide-price-editor :sku-id="selectedSkuId" />
        </a-tab-pane>
        <a-tab-pane key="stock" tab="库存台账">
          <div class="section-toolbar">
            <div><h3>库存台账</h3><span>可用量 = 实物数量 − 已占用数量</span></div>
            <div class="toolbar-actions">
              <a-select v-model="stockWarehouseId" allow-clear style="width:210px" placeholder="全部仓库" @change="loadBalances">
                <a-select-option v-for="wh in enabledWarehouses" :key="wh.id" :value="wh.id">{{ wh.warehouseName }}</a-select-option>
              </a-select>
              <a-button type="primary" icon="arrow-right" @click="$router.push('/erp/stock-opening')">进入库存期初</a-button>
            </div>
          </div>
          <a-table :columns="balanceColumns" :data-source="balances" :loading="balanceLoading" row-key="id" :pagination="{ pageSize: 20 }" :locale="{ emptyText: '暂无库存台账，请先录入期初库存' }">
            <template slot="qty" slot-scope="value">{{ Number(value || 0).toFixed(3) }}</template>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </div>
  </page-header-wrapper>
</template>

<script>
import GuidePriceEditor from '@/views/erp/product/GuidePriceEditor.vue'
import { skuOptions } from '@/api/erp/salesOrder'
import { listWarehouse, saveWarehouse, setWarehouseEnabled } from '@/api/erp/warehouse'
import { listStockBalance } from '@/api/erp/stock'

const SECTION_ROUTES = {
  warehouse: '/erp/warehouse',
  price: '/erp/guide-price',
  stock: '/erp/stock-ledger'
}

const ROUTE_SECTIONS = {
  '/erp/warehouse': 'warehouse',
  '/erp/guide-price': 'price',
  '/erp/stock-ledger': 'stock'
}

const SECTION_META = {
  warehouse: { title: '仓库档案', description: '维护销售出库与库存业务可使用的仓库。' },
  price: { title: '商品指导价', description: '按 SKU 和仓库维护销售指导价，供销售订单选择。' },
  stock: { title: '库存台账', description: '查看各仓库、SKU 的实物数量、占用数量和可用数量。' }
}

export default {
  name: 'ErpSalesSetup',
  components: { GuidePriceEditor },
  data () {
    return {
      activeTab: ROUTE_SECTIONS[this.$route.path] || 'warehouse', warehouses: [], warehouseLoading: false,
      warehouseSaving: false, warehouseEditing: false, warehouseDraft: {},
      selectedSkuId: undefined, skuList: [], stockWarehouseId: undefined,
      balances: [], balanceLoading: false,
      warehouseColumns: [
        { title: '仓库编码', dataIndex: 'warehouseCode', width: 180 },
        { title: '仓库名称', dataIndex: 'warehouseName', width: 230 },
        { title: '状态', dataIndex: 'status', width: 100, scopedSlots: { customRender: 'warehouseStatus' } },
        { title: '备注', dataIndex: 'remark' },
        { title: '操作', dataIndex: 'operation', width: 150, scopedSlots: { customRender: 'warehouseActions' } }
      ],
      balanceColumns: [
        { title: '仓库', dataIndex: 'warehouseName', width: 170 },
        { title: 'SKU编码', dataIndex: 'skuCode', width: 170 },
        { title: '商品名称', dataIndex: 'skuName' },
        { title: '实物数量', dataIndex: 'qtyBalance', width: 130, scopedSlots: { customRender: 'qty' } },
        { title: '已占用', dataIndex: 'qtyOccupied', width: 120, scopedSlots: { customRender: 'qty' } },
        { title: '可用数量', dataIndex: 'qtyAvailable', width: 130, scopedSlots: { customRender: 'qty' } },
        { title: '单位', dataIndex: 'unitName', width: 90 }
      ]
    }
  },
  computed: {
    enabledWarehouses () { return this.warehouses.filter(wh => wh.status === 'enabled') },
    sectionMeta () { return SECTION_META[this.activeTab] || SECTION_META.warehouse }
  },
  watch: {
    '$route.path' (path) { this.activeTab = ROUTE_SECTIONS[path] || 'warehouse' }
  },
  created () { this.loadWarehouses(); this.loadBalances(); this.searchSku('') },
  methods: {
    handleTabChange (key) {
      const target = SECTION_ROUTES[key]
      if (target && target !== this.$route.path) this.$router.push(target)
    },
    loadWarehouses () {
      this.warehouseLoading = true
      listWarehouse().then(res => { this.warehouses = res.data || [] }).finally(() => { this.warehouseLoading = false })
    },
    startWarehouse () { this.warehouseDraft = { warehouseCode: '', warehouseName: '', operatingSubjectId: 0 }; this.warehouseEditing = true },
    editWarehouse (record) { this.warehouseDraft = { ...record }; this.warehouseEditing = true },
    submitWarehouse () {
      if (!this.warehouseDraft.warehouseCode || !this.warehouseDraft.warehouseName) return this.$message.warning('请填写仓库编码和名称')
      this.warehouseSaving = true
      saveWarehouse(this.warehouseDraft).then(() => {
        this.$message.success('仓库档案已保存')
        this.warehouseEditing = false
        this.loadWarehouses()
      }).finally(() => { this.warehouseSaving = false })
    },
    toggleWarehouse (record) {
      const enabled = record.status !== 'enabled'
      this.$confirm({
        title: enabled ? '启用仓库' : '停用仓库',
        content: enabled ? '启用后可用于新销售订单。' : '有库存或未完成订单时将无法停用。',
        onOk: () => setWarehouseEnabled(record.id, enabled).then(() => this.loadWarehouses())
      })
    },
    searchSku (keyword) {
      skuOptions(keyword).then(res => {
        const options = res.data || []
        const current = this.skuList.find(sku => String(sku.id) === String(this.selectedSkuId))
        this.skuList = current && !options.some(sku => String(sku.id) === String(current.id)) ? [current, ...options] : options
      })
    },
    loadBalances () {
      this.balanceLoading = true
      listStockBalance({ warehouseId: this.stockWarehouseId }).then(res => { this.balances = res.data || [] }).finally(() => { this.balanceLoading = false })
    }
  }
}
</script>

<style scoped>
.sales-setup{background:#fcfdff;border:1px solid #e4e9ef;border-radius:8px;padding:22px 24px;min-height:630px}
.setup-heading{margin-bottom:22px}.setup-heading h2{font-size:19px;font-weight:650;color:#263445;margin:0 0 5px}.setup-heading p{font-size:13px;color:#68788b;margin:0}
.section-toolbar{display:flex;align-items:center;justify-content:space-between;gap:16px;margin:4px 0 16px}
.section-toolbar h3,.opening-history h3{display:inline-block;margin:0 10px 0 0;color:#263445;font-size:15px;font-weight:650}
.section-toolbar span{color:#768599;font-size:12px}.toolbar-actions{display:flex;gap:8px}
.inline-editor,.opening-editor{border:1px solid #e4e9ef;background:#f5f7fa;border-radius:6px;padding:14px;margin-bottom:16px}
.inline-editor{display:flex;gap:8px}.inline-editor /deep/ .ant-input{max-width:230px}
.opening-editor strong{color:#263445}.opening-editor p{color:#758497;font-size:12px;margin:3px 0 12px}.opening-fields{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.opening-history{margin-top:30px;border-top:1px solid #e9edf2;padding-top:20px}.opening-history h3{margin-bottom:12px}
@media(max-width:900px){.sales-setup{padding:16px}.section-toolbar,.inline-editor{align-items:stretch;flex-direction:column}.toolbar-actions{flex-wrap:wrap}}
</style>
